import { Component, ChangeDetectionStrategy, Input, ViewChild, inject, EventEmitter, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Observer } from 'rxjs';
import { TuiDialogContext, TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusContent } from '@taiga-ui/polymorpheus';
import { TranslateService } from '@ngx-translate/core';
import { RouteDefinition, getInnerRouteDefinitionFromLink, isEmailUrl, isPhoneUrl, isValidUrl } from '@src/utils';
import { Nullable, Optional } from '@src/types/utils';
import { EnvService } from '@src/app/modules/env';
import { UrlPipe } from '@src/app/shared/pipes';

import { LinkColor, UrlType } from './types';

/**
 * Компонент для отображения ссылки
 * @param {string} url - ссылка
 * @param {string} displayText - текст, отображаемый вместо ссылки
 * @param {boolean} disableRedirectDialog - отключить подтверждение перехода по внешней ссылке (по-умолчанию false)
 * @param {string} iconStart - название иконки перед текстом
 * @param {string} iconEnd - название иконки после текста
 * @param {boolean} pseudo - признак отображения нижнего пунктирного подчёркивания (по-умолчанию false)
 * @param {LinkColor} color - цвет ссылки ("grey" | "blue" | "red" по-умолчанию 'blue')
 */

// TODO: принимать ссылку без домена. Пример: /association?menu=users
@Component({
  selector: 'app-link',
  templateUrl: './link.component.html',
  styleUrls: ['./link.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LinkComponent {
  private readonly env = inject(EnvService);
  private readonly urlPipe = inject(UrlPipe);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly dialogService = inject(TuiDialogService);

  @Input() set url(value: Optional<Nullable<string>>) {
    this.importValue = value;
    this.normalizedUrl = this.urlPipe.transform(value) ?? '';
    this.routeDefinition = getInnerRouteDefinitionFromLink(value ?? '');
  }
  @Input() displayText: Optional<Nullable<string>> = null;
  @Input() disableRedirectDialog = false;

  @Input() iconStart = '';
  @Input() iconEnd = '';
  @Input() pseudo = false;
  @Input() color: LinkColor = 'blue';

  @Output() externalLink: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild('tmpRedirectToUrl') redirectToUrlContent?: PolymorpheusContent<TuiDialogContext>;

  normalizedUrl = '';
  importValue?: Nullable<string>;

  private routeDefinition: RouteDefinition | null = null;

  get innerLink() {
    return this.routeDefinition?.innerLink;
  }

  get urlType(): UrlType {
    return this.routeDefinition === null ? 'external' : 'internal';
  }

  get normalizedDisplayableText(): string {
    if (typeof this.displayText === 'string') {
      return this.displayText;
    }

    if (this.routeDefinition?.title) {
      return this.translateService.instant(this.routeDefinition.title);
    }

    const validUrl = isValidUrl(this.normalizedUrl);

    if (validUrl) {
      const displayableUrl = new URL(validUrl);
      return displayableUrl.origin + displayableUrl.pathname;
    }

    return this.importValue ?? '';
  }

  openLink() {
    if (!this.normalizedUrl) return;

    if (
      !this.disableRedirectDialog &&
      !this.env.isBot &&
      !isPhoneUrl(this.normalizedUrl) &&
      !isEmailUrl(this.normalizedUrl)
    ) {
      // Под ботом не обрабатываем нажатие по ссылке, т.к. Telegram сам спрашивает подтверждение перехода
      this.showPrompt();
      return;
    }

    this.env.openLink(this.normalizedUrl);
  }

  gotoInnerLink() {
    if (!this.routeDefinition?.innerLink) return;

    this.router.navigateByUrl(this.routeDefinition.innerLink);
  }

  gotoExternalLink(observer: Observer<void>) {
    observer.complete();
    this.externalLink.emit(this.normalizedUrl);

    setTimeout(() => {
      this.env.openLink(this.normalizedUrl);
    }, 300);
  }

  private showPrompt() {
    if (!this.redirectToUrlContent) {
      return;
    }

    this.dialogService
      .open(this.redirectToUrlContent, {
        closeable: false,
        dismissible: true,
      })
      .subscribe();
  }
}
